import _ from "lodash";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { TickCircleOutlined } from "@toddle-edu/ds-icons";
import { Modal, Button, SelectDropdown } from "@toddle-edu/ds-web";

import { useI18n } from "Hooks";
import { getUserInfofromStorage } from "services/localStorage";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import OTPInput from "routes/components/MultiFactorAuthentication/components/OTPInput";
import {
  updateMfaVerificationDetails,
  validateUser,
  verifyMfa,
  generateMfaOtp,
  setToastMsg,
  onSwitchToOrganizationLocally,
  setUserFromIdentityPoolUserList,
} from "Login/modules/LoginModule";
import {
  verifyUserMfa,
  sendOtpForMfaVerification,
} from "routes/components/MultiFactorAuthentication/modules/MultiFactorAuthenticationModule";
import { getMfaMethodOptions } from "routes/components/MultiFactorAuthentication/modules/MultiFactorAuthenticationUtils";
import { useResendCooldown } from "routes/components/MultiFactorAuthentication/modules/MultiFactorAuthenticationHooks";

import classes from "./VerificationAuthModal.scss";

const VIEW_STATES = {
  DEFAULT: "DEFAULT",
  CODE_SENT: "CODE_SENT",
  VERIFYING: "VERIFYING",
  SUCCESS: "SUCCESS",
};

const SUCCESS_REDIRECT_DELAY = 1500;
const EMAIL_OTP_METHOD = "EMAIL_OTP";

const VerificationAuthModal = props => {
  const { t } = useI18n();

  const {
    verifyMfa,
    validateUser,
    mfaVerificationDetails,
    updateMfaVerificationDetails,
    isOrgLevel = false,
    verifyUserMfa,
    onSwitchToOrganizationLocally,
    setUserFromIdentityPoolUserList,
    setToastMsg,
    generateMfaOtp,
    sendOtpForMfaVerification,
  } = props;

  const {
    mfaToken,
    userType,
    organization,
    userId,
    identityId,
    designation,
    emailSource,
    isUserLoggedIn,
    mfaEnabledMethods: mfaEnabledMethodConfigs = [],
  } = mfaVerificationDetails;

  const mfaEnabledMethods = _.map(mfaEnabledMethodConfigs, "name");
  const maskedEmail = _.get(
    _.find(mfaEnabledMethodConfigs, { name: EMAIL_OTP_METHOD }),
    "metadata.email",
    ""
  );

  const isEmailOtpMethod = method => method === EMAIL_OTP_METHOD;
  const isSingleMethod = _.size(mfaEnabledMethods) === 1;
  const initialMethod = mfaEnabledMethods[0];

  const [verificationCode, setVerificationCode] = useState("");
  const [selectedMethod, setSelectedMethod] = useState(initialMethod);
  const [viewState, setViewState] = useState(VIEW_STATES.DEFAULT);

  const successTimerRef = useRef(null);
  const hasAutoSentOtpRef = useRef(false);
  const { resendCooldown, startResendCooldown } = useResendCooldown();

  const methodOptions = getMfaMethodOptions(mfaEnabledMethods, t);
  const selectedMethodOption =
    _.find(methodOptions, { value: selectedMethod }) || null;

  const orgName = _.get(
    organization,
    "name",
    t("common:your_with_label", {
      label: t("common:organization"),
    })
  );
  const hasMultipleMethods = _.size(mfaEnabledMethods) > 1;
  const isEmailOtp = isEmailOtpMethod(selectedMethod);
  const isVerifying = viewState === VIEW_STATES.VERIFYING;
  const isSuccess = viewState === VIEW_STATES.SUCCESS;
  const isCodeSent = viewState === VIEW_STATES.CODE_SENT;

  const showOtpInput = !isEmailOtp || isCodeSent;
  const showSendButton = isEmailOtp && !isCodeSent && !isVerifying;

  const handleSendOtp = useCallback(async () => {
    setViewState(VIEW_STATES.VERIFYING);

    try {
      let regenOTPTime = 0;

      if (isOrgLevel && isUserLoggedIn) {
        const result = await sendOtpForMfaVerification({
          userId,
          userType,
          organizationId: _.get(organization, "id"),
          method: selectedMethod,
        });
        regenOTPTime = _.get(result, "regenOTPTime", 0);

        if (!_.get(result, "isSuccess", false) && regenOTPTime === 0) {
          setViewState(VIEW_STATES.DEFAULT);
          setToastMsg("toastMsgs:something_went_wrong");
          return;
        }
      } else {
        let data;
        if (isOrgLevel) {
          const { token } = getUserInfofromStorage() || {};
          data = {
            userType,
            userId,
            token,
            method: selectedMethod,
            organizationId: _.get(organization, "id"),
          };
        } else {
          data = {
            mfaToken,
            method: selectedMethod,
          };
        }

        const result = await generateMfaOtp(data);
        regenOTPTime = _.get(result, "regenOTPTime", 0);

        if (!_.get(result, "isSuccess", false) && regenOTPTime === 0) {
          setViewState(VIEW_STATES.DEFAULT);
          setToastMsg("toastMsgs:something_went_wrong");
          return;
        }
      }

      const cooldownSeconds = _.ceil(regenOTPTime / 1000);

      setViewState(VIEW_STATES.CODE_SENT);
      startResendCooldown(cooldownSeconds);
    } catch {
      setViewState(VIEW_STATES.DEFAULT);
      setToastMsg("toastMsgs:something_went_wrong");
    }
  }, [
    isOrgLevel,
    selectedMethod,
    mfaToken,
    userId,
    userType,
    organization,
    sendOtpForMfaVerification,
    generateMfaOtp,
    setToastMsg,
    startResendCooldown,
  ]);

  const handleResendOtp = () => {
    if (resendCooldown > 0) return;
    handleSendOtp();
  };

  useEffect(() => {
    if (hasAutoSentOtpRef.current) return;

    if (isEmailOtpMethod(initialMethod) && isSingleMethod) {
      hasAutoSentOtpRef.current = true;
      handleSendOtp();
    }
  }, [initialMethod, isSingleMethod, handleSendOtp]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleVerificationSuccess = ({ validateParams, switchParams }) => {
    setViewState(VIEW_STATES.SUCCESS);
    successTimerRef.current = setTimeout(() => {
      if (validateParams) {
        validateUser(validateParams);
        updateMfaVerificationDetails({ isVisible: false });
      } else if (switchParams) {
        onSwitchToOrganizationLocally(switchParams);
      }
    }, SUCCESS_REDIRECT_DELAY);
  };

  const handleVerifyMfa = async () => {
    setViewState(VIEW_STATES.VERIFYING);
    const response = await verifyMfa({
      authenticationCode: verificationCode,
      mfaToken,
      method: selectedMethod,
    });

    if (_.has(response, "token")) {
      const token = _.get(response, "token", "");
      const data = _.get(response, "data", {});

      handleVerificationSuccess({
        validateParams: { data, token, identityUserType: userType },
      });
    } else {
      setViewState(isEmailOtp ? VIEW_STATES.CODE_SENT : VIEW_STATES.DEFAULT);
      setToastMsg({ msg: "toastMsgs:invalid_auth_code", type: "error" });
    }
  };

  const handleVerifyUserMfa = async () => {
    setViewState(VIEW_STATES.VERIFYING);
    const input = {
      authenticationCode: verificationCode,
      userId,
      organizationId: _.get(organization, "id"),
      userType,
      method: selectedMethod,
    };

    const jwt = await verifyUserMfa(input);

    if (jwt) {
      handleVerificationSuccess({
        switchParams: {
          type: userType,
          organization,
          id: userId,
          identityId,
          designation,
          emailSource,
          jwt,
        },
      });
    } else {
      setViewState(isEmailOtp ? VIEW_STATES.CODE_SENT : VIEW_STATES.DEFAULT);
      setToastMsg({ msg: "toastMsgs:invalid_auth_code", type: "error" });
    }
  };

  const handleGetUserDetailsFromIdentity = () => {
    setUserFromIdentityPoolUserList({
      userType,
      id: userId,
      identityId,
      authenticationCode: verificationCode,
      shouldCloseVerificationModal: true,
      method: selectedMethod,
    });
  };

  const handleSubmit = () => {
    if (!verificationCode) return;

    if (isOrgLevel) {
      if (isUserLoggedIn) {
        handleVerifyUserMfa();
      } else {
        handleGetUserDetailsFromIdentity();
      }
    } else {
      handleVerifyMfa();
    }
  };

  const handleMethodChange = option => {
    setSelectedMethod(option.value);
    setVerificationCode("");
    setViewState(VIEW_STATES.DEFAULT);
  };

  const handleClose = () => {
    if (!isVerifying && !isSuccess) {
      updateMfaVerificationDetails({ isVisible: false });
    }
  };

  const getSubtitle = () => {
    if (isEmailOtp && (isCodeSent || isSingleMethod)) {
      return t("common:enter_email_otp_subtitle", { email: maskedEmail });
    }
    if (isEmailOtp) {
      return t("common:verification_code_will_be_sent", {
        email: maskedEmail,
      });
    }
    if (selectedMethod === "BACKUP_CODE") {
      return t("common:enter_backup_code_subtitle");
    }
    if (selectedMethod === "TOTP") {
      return t("common:enter_totp_subtitle");
    }
    return t("common:authenticate_using_preferred_method");
  };

  const subtitle = getSubtitle();

  if (isSuccess) {
    return (
      <Modal
        dsVersion="2.0"
        height="fit-content"
        width="400px"
        isOpen={true}
        shouldCloseOnEsc={false}
      >
        <Modal.Body>
          <div className={classes.successContainer}>
            <div className={classes.successCircle}>
              <TickCircleOutlined size="large" variant="success" />
            </div>
            <span className={`${classes.successTitle} text-label-l`}>
              {t("common:verified_successfully")}
            </span>
            <span className={`${classes.successMessage} text-body`}>
              {t("common:redirecting_to", { orgName })}
            </span>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        dsVersion="2.0"
        height="fit-content"
        width="400px"
        isOpen={true}
        onClose={handleClose}
        shouldCloseOnEsc={!isVerifying}
      >
        <Modal.Header
          isClosable={!isVerifying}
          title={t("common:two_factor_authentication")}
          subtitle={subtitle}
        />
        <Modal.Body>
          <div className={classes.bodyContainer}>
            {hasMultipleMethods && (
              <SelectDropdown
                dsVersion="2.0"
                size="medium"
                label={t("common:verification_method")}
                options={methodOptions}
                value={selectedMethodOption}
                onChange={handleMethodChange}
                isClearable={false}
                isCreatable={false}
                isDisabled={isVerifying}
                hasMultilineOptions={true}
              />
            )}
            {(showOtpInput || isVerifying) && (
              <div className={classes.inputSection}>
                <span className={`${classes.inputLabel} text-label`}>
                  {t("common:enter_code")}
                </span>
                <OTPInput
                  value={verificationCode}
                  onChange={setVerificationCode}
                  onSubmit={handleSubmit}
                  disabled={isVerifying}
                  autoFocus={true}
                  allowAlphanumeric={selectedMethod === "BACKUP_CODE"}
                />
                {isEmailOtp && (
                  <div className={classes.resendButtonWrapper}>
                    <Button
                      dsVersion="2.0"
                      variant="neutral"
                      type="inline"
                      size="small"
                      onClick={handleResendOtp}
                      disabled={resendCooldown > 0}
                    >
                      {resendCooldown > 0
                        ? `${t("common:resend_code")} (${resendCooldown}s)`
                        : t("common:resend_code")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.buttonContainer}>
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={handleClose}
              disabled={isVerifying}
            >
              {t("common:cancel")}
            </Button>
            {showSendButton ? (
              <Button
                dsVersion="2.0"
                variant="progressive"
                size="medium"
                onClick={handleSendOtp}
              >
                {t("common:send_verification_code")}
              </Button>
            ) : (
              <Button
                dsVersion="2.0"
                variant="progressive"
                size="medium"
                disabled={
                  !_.isEqual(_.size(verificationCode), 6) || isVerifying
                }
                onClick={handleSubmit}
              >
                {t("common:verify")}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
      {isVerifying ? <FullScreenLoader /> : null}
    </>
  );
};

const mapStateToProps = state => {
  const mfaVerificationDetails = _.get(
    state,
    "login.mfaVerificationDetails",
    {}
  );

  return {
    mfaVerificationDetails,
  };
};

const mapActionCreators = {
  updateMfaVerificationDetails,
  verifyMfa,
  generateMfaOtp,
  validateUser,
  setToastMsg,
  verifyUserMfa,
  sendOtpForMfaVerification,
  onSwitchToOrganizationLocally,
  setUserFromIdentityPoolUserList,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(VerificationAuthModal);
